import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 10000
});

/* ================= REQUEST INTERCEPTOR ================= */
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* ================= RESPONSE INTERCEPTOR ================= */
let sessionExpired = false;

api.interceptors.response.use(
  (response) => {
    const message = response?.data?.message?.toLowerCase();

    if (
      message?.includes('jwt expired') 
      || message?.includes('token expired') 
      || message?.includes('token mismatch')
    ) {
      if (!sessionExpired) {
        sessionExpired = true;

        window.dispatchEvent(new Event('sessionExpired'));
      }
    }

    return response;
  },

  (error) => {
    const status = error?.response?.status;

    const normalizedError = {
      message:
        error.response?.data?.message
        || error.message
        || 'Something went wrong',
      status: status || 500
    };

    console.error('[API ERROR]', normalizedError);

    if ((status === 401 || status === 403 || status === 500) && !sessionExpired) {
      sessionExpired = true;

      window.dispatchEvent(new Event('sessionExpired'));
    }

    return Promise.reject(normalizedError);
  }
);

export default api;

const sleep = (ms: number) => new Promise<void>((resolve) => {
  setTimeout(resolve, ms);
});

/* eslint-disable no-await-in-loop */
export const apiWithRetry = async (
  requestFn: () => Promise<any>,
  retries = 3,
  delay = 1000
) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await requestFn();
    } catch (err: any) {
      if (err?.status === 401 || err?.message?.includes('jwt expired')) {
        throw err;
      }

      if (i === retries - 1) throw err;
      await sleep(delay);
    }
  }
};

/* eslint-disable no-await-in-loop */
export const pollApi = async (
  requestFn: () => Promise<any>,
  retries = 3,
  interval = 3000
) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await requestFn();
    } catch {
      if (i === retries - 1) throw new Error('Polling failed');
      await sleep(interval);
    }
  }
};
